'use client';

import { useCallback, useEffect, useState } from 'react';

const isStillDismissed = (stored: string | null, ttlMs?: number) => {
  if (ttlMs === undefined) return stored === 'true';
  const at = Number(stored);
  return !!at && Date.now() - at < ttlMs;
};

// With a ttl the key holds the time of the dismissal, which runs out; without
// one it holds 'true' for good.
export const useStoredDismissal = (
  key: string,
  ttlMs?: number
): [boolean, () => void] => {
  // Default to true so the offer stays hidden until the effect reads
  // localStorage, preventing a flash of it during hydration.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(isStillDismissed(localStorage.getItem(key), ttlMs));
    } catch {
      setDismissed(false);
    }
  }, [key, ttlMs]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(
        key,
        ttlMs === undefined ? 'true' : String(Date.now())
      );
    } catch {
      // Storage unavailable (private mode / quota): hidden for this page only.
    }
    setDismissed(true);
  }, [key, ttlMs]);

  return [dismissed, dismiss];
};
